import { EMPTY_OBJECT } from '../../../constants/common'
import { LOCAL_STORAGE_BIRDVIEW_CHARTS_HIDDEN_KEY } from '../../../constants/storage'
import { EMPTY_SECTION } from '../DashboardBirdview.constants'
import {
  getBirdviewLoadState,
  getBirdviewSectionState,
} from '../DashboardBirdview.utils'
import {
  formatBirdviewKpiValue,
  getBirdviewTrend,
} from '../DashboardBirdview.utils.charts'
import {
  getBirdviewStorageItemSetKey,
  getBirdviewStorageKey,
} from '../DashboardBirdview.utils.storage'

import {
  BIRDVIEW_ANALYTICS_CHARTS,
  BIRDVIEW_ANALYTICS_SOON_CHARTS,
  BIRDVIEW_KPI_DEFINITIONS,
  BirdviewChartTitlePrefix,
} from './BirdviewAnalytics.constants'

function getBirdviewChartSection({ chart, section }) {
  const chartData = section.data?.[chart.dataKey] ?? EMPTY_SECTION

  return {
    items: chartData.items,
    reload: section.reload,
    state: getBirdviewSectionState({
      hasError: section.hasError,
      isLoading: section.isLoading,
      items: chartData.items,
    }),
    total: chartData.total,
  }
}

function formatBirdviewAnalyticsChartTitle({ chart, isTeamScope, t }) {
  const title = t(chart.titleKey)

  switch (chart.titlePrefix) {
    case BirdviewChartTitlePrefix.Scope:
      return t(
        isTeamScope
          ? 'DashboardBirdview.TeamChartTitleKey'
          : 'DashboardBirdview.MyChartTitleKey',
        { title }
      )
    case BirdviewChartTitlePrefix.Requests:
      return t('DashboardBirdview.RequestsChartTitleKey', { title })
    default:
      return title
  }
}

function toBirdviewAnalyticsKpiDelta({ definition, t, values }) {
  return {
    direction: getBirdviewTrend(values.delta),
    text: formatBirdviewKpiValue({
      t,
      unit: definition.deltaUnit ?? definition.unit,
      value: Math.abs(values.delta),
    }),
  }
}

function toBirdviewAnalyticsKpiValues({ definition, t, values }) {
  return {
    count: formatBirdviewKpiValue({
      t,
      unit: definition.unit,
      value: values.value,
    }),
    delta: toBirdviewAnalyticsKpiDelta({ definition, t, values }),
    deltaTone: values.tone,
    points: values.points,
    tone: values.tone,
  }
}

// A tile exists before its numbers arrive, so the row keeps its shape while
// the section loads or fails.
function toBirdviewAnalyticsKpi({ definition, state, t, values }) {
  return {
    ...definition,
    ...(values
      ? toBirdviewAnalyticsKpiValues({ definition, t, values })
      : null),
    label: t(definition.labelKey),
    state,
  }
}

// A chart whose section the loaders did not register has no card.
export function getBirdviewAnalyticsCharts({
  isTeamScope,
  preset,
  sections,
  t,
}) {
  return BIRDVIEW_ANALYTICS_CHARTS[preset]
    .filter(chart => sections[chart.sectionKey])
    .map(chart => ({
      ...chart,
      section: getBirdviewChartSection({
        chart,
        section: sections[chart.sectionKey],
      }),
      title: formatBirdviewAnalyticsChartTitle({ chart, isTeamScope, t }),
    }))
}

export function getBirdviewAnalyticsKpis({
  preset,
  section = EMPTY_OBJECT,
  t,
}) {
  const state = getBirdviewLoadState(section)

  return BIRDVIEW_KPI_DEFINITIONS[preset].map(definition =>
    toBirdviewAnalyticsKpi({
      definition,
      state,
      t,
      values: section.data?.[definition.id],
    })
  )
}

export function getBirdviewAnalyticsHiddenStorageKey({ ids, storageScope }) {
  return getBirdviewStorageKey(LOCAL_STORAGE_BIRDVIEW_CHARTS_HIDDEN_KEY, [
    storageScope,
    getBirdviewStorageItemSetKey(ids),
  ])
}

export function getBirdviewAnalyticsCatalogRows({ charts, hiddenIds, t }) {
  return charts.map(chart => ({
    id: chart.id,
    isShown: !hiddenIds.includes(chart.id),
    object: chart.object,
    question: t(chart.questionKey),
    title: chart.title,
  }))
}

export function getBirdviewAnalyticsSoonRows({ preset, t }) {
  return BIRDVIEW_ANALYTICS_SOON_CHARTS[preset].map(chart => ({
    id: chart.id,
    isSoon: true,
    object: chart.object,
    question: t(chart.questionKey),
    title: t(chart.titleKey),
  }))
}

export function getBirdviewAnalyticsHiddenRows(rows) {
  return rows.filter(row => !row.isShown)
}
